import { animate, state, style, transition, trigger } from '@angular/animations';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  HostBinding,
  OnInit,
  signal,
  inject,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { HomesFacade } from '@library/store/homes/homes.facade';
import { IconModule } from '@library/ui/atoms/icon/icon.module';
import { TextModule } from '@library/ui/atoms/text/text.module';
import { LangPipe } from '@library/utils/pipes/lang.pipe';
import { PipesModule } from '@library/utils/pipes/pipes.module';
import { WeathermapFacade } from '@store/weathermap.facade';
import { Station } from '@store/weathermap.store';
import { firstValueFrom, map } from 'rxjs';
import { EventManagerService, EventSource } from 'src/app/services/event-manager.service';
import { LoginService } from 'src/app/services/login.service';
import { SelectedStationService } from 'src/app/services/selected-station.service';
import { UserDataService } from 'src/app/services/user-data.service';
import { ForecastComponent } from 'src/app/side-panel/forecast/forecast.component';
import { HumidityCardComponent } from 'src/app/side-panel/humidity-card/humidity-card.component';
import { PressureCardComponent } from 'src/app/side-panel/pressure-card/pressure-card.component';
import { RainCardComponent } from 'src/app/side-panel/rain-card/rain-card.component';
import { TemperatureCardComponent } from 'src/app/side-panel/temperature-card/temperature-card.component';
import { WindCardComponent } from 'src/app/side-panel/wind-card/wind-card.component';

@Component({
  selector: 'app-side-panel',
  imports: [
    IconModule,
    TextModule,
    TemperatureCardComponent,
    HumidityCardComponent,
    PressureCardComponent,
    WindCardComponent,
    RainCardComponent,
    ForecastComponent,
    PipesModule,
  ],
  providers: [],
  templateUrl: './side-panel.component.html',
  styleUrl: './side-panel.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {},
  animations: [
    trigger('slideInOut', [
      state(
        'closed',
        style({
          transform: 'translateX(calc(100% + 32px))',
          opacity: 0,
        }),
      ),
      state(
        'opened',
        style({
          transform: 'translateX(0)',
          opacity: 1,
        }),
      ),
      transition('closed <=> opened', animate('150ms ease-in-out')),
    ]),
    trigger('openClose', [
      state(
        'closed',
        style({
          opacity: 0,
          height: '0',
        }),
      ),
      state(
        'opened',
        style({
          opacity: 1,
          height: '*',
        }),
      ),
      transition('closed <=> opened', animate('150ms ease-in-out')),
    ]),
  ],
})
export class SidePanelComponent implements OnInit {
  private readonly weathermapFacade = inject(WeathermapFacade);
  private readonly langPipe = inject(LangPipe);
  private readonly eventManagerService = inject(EventManagerService);
  private readonly selectedStationService = inject(SelectedStationService);
  private readonly loginService = inject(LoginService);
  private readonly homesFacade = inject(HomesFacade);
  private readonly userDataService = inject(UserDataService);

  station = this.weathermapFacade.selectedStation;

  shareOpenState = signal('closed');

  linkCopied = signal(false);

  shareLink = computed(() => {
    const station = this.weathermapFacade.selectedStation();

    return `${window.location.origin}/?stationid=${station.id}&zoom=${window.map.getZoom()}`;
  });

  closeOthers = effect(
    () => {
      if (this.station()) {
        this.eventManagerService.closeOtherSources(EventSource.PANEL);
      }
    },
    { allowSignalWrites: true },
  );

  hasOutdoor = computed(() => {
    const modules = this.weathermapFacade.selectedStation().modules;
    return modules.some((module) => module.type === 'NAModule1');
  });

  hasWind = computed(() => {
    const modules = this.weathermapFacade.selectedStation().modules;
    return (
      modules.some((module) => module.type === 'NAModule2') &&
      this.weathermapFacade.selectedStation().WindStrength?.length > 0
    );
  });

  hasRain = computed(() => {
    const modules = this.weathermapFacade.selectedStation().modules;
    return (
      modules.some((module) => module.type === 'NAModule3') && this.weathermapFacade.selectedStation().Rain?.length > 0
    );
  });

  state: 'opened' | 'closed';
  @HostBinding('@slideInOut') get slideInOut(): string {
    return this.station() ? 'opened' : 'closed';
  }

  loggedIn = this.loginService.loggedIn;

  favoriteStations = toSignal(
    this.homesFacade.homes$.pipe(map((stations) => stations.filter((station) => station.mode === 'favorite'))),
  );

  isFavorite = computed(() => {
    const favoriteStations = this.favoriteStations();
    const selectedStation = this.station();
    return favoriteStations.some((station) => station.id === selectedStation.id);
  });

  loading = signal(false);

  lastUpdate = computed<string>(() => {
    const lastUpdate = this.getStationLastUpdate(this.station());
    const currentTime = Math.floor(new Date().getTime() / 1000);
    const interval = currentTime - lastUpdate;

    if (interval < 60) {
      return this.langPipe.transform('common.__COM_LAST_UPDATE_A_MOMENT_AGO');
    } else if (interval < 3600) {
      return this.langPipe.transform('app.__LASTUPDATE_MINUTESAGO', [Math.floor(interval / 60).toFixed(0)]);
    } else {
      return this.langPipe.transform('app.__TIMER_LONG_TIME_AGO');
    }
  });

  ngOnInit(): void {
    this.eventManagerService.registerSource(EventSource.PANEL, () => {
      this.close();
    });
  }

  close(): void {
    this.selectedStationService.unselectStation();
    this.shareOpenState.set('closed');
  }

  toggleSharePanel(): void {
    this.shareOpenState.update((state) => {
      return state === 'closed' ? 'opened' : 'closed';
    });
  }

  async copyLink(): Promise<void> {
    const type = 'text/plain';
    const clipboardItemData = {
      [type]: this.shareLink(),
    };
    const clipboardItem = new ClipboardItem(clipboardItemData);
    await navigator.clipboard.write([clipboardItem]);
    this.linkCopied.set(true);
  }

  async toggleFavorite(): Promise<void> {
    this.loading.set(true);
    if (this.isFavorite()) {
      await firstValueFrom(this.weathermapFacade.dissociateFavoriteStation(this.station()));
      this.homesFacade.removeHome(this.station().id);
    } else {
      await firstValueFrom(this.weathermapFacade.associateFavoriteStation(this.station()));
      await this.userDataService.loadHomes();
    }
    this.loading.set(false);
  }

  getStationLastUpdate(station: Station): number {
    const lastUpdates = [
      station.Temperature?.[station.Temperature.length - 1].x,
      station.WindStrength?.[station.WindStrength.length - 1].x,
      station.WindAngle?.[station.WindStrength.length - 1].x,
      station.GustStrength?.[station.GustStrength.length - 1].x,
      station.GustAngle?.[station.GustAngle.length - 1].x,
      station.Rain?.[station.Rain.length - 1].x,
      station.Humidity?.[station.Humidity.length - 1].x,
      station.Pressure?.[station.Pressure.length - 1].x,
    ];
    const lastUpdate = Math.max(...lastUpdates.filter((x) => x !== undefined));

    return lastUpdate;
  }
}
